import Auid from 'Auid';
import BuildConfig from 'BuildConfig';
import { getProgramConfigValue } from 'Program';
import { AdmissionOfferStatus } from 'AdmissionOffer';
import { type StatsigUser } from '@statsig/js-client';
import { BROWSER_AND_BUILD_INFO } from 'TinyEventLogger';
import { type CurrentUser } from './types';
import { getUserProgramState } from './BaseUser';

// See also StatsigMixin#as_statsig
export function asStatsig(user: CurrentUser | null | undefined): StatsigUser {
    if (!user) return { userID: Auid.ensure(), appVersion: BuildConfig.build_number };

    return {
        userID: user.id,
        appVersion: BuildConfig.build_number,
        custom: {
            // 'android' or 'ios' inside the Capacitor applications, or 'web' for everywhere else.
            // Note that this still leaves a hole, particularly in identifying mobile web on Android or iOS, but we've not yet needed to make that distinction.
            client: BROWSER_AND_BUILD_INFO.client,
            cordova: !!window.CORDOVA,
            created_at: user.createdAt,
            paid_student_conversion_status: getPaidStudentConversionStatus(user),
            sign_up_code: user.signUpCode || '',
        },
        privateAttributes: {
            email: user.email || '',
        },
    };
}

function getPaidStudentConversionStatus(user: CurrentUser) {
    const hasUpsThatSupportsApplications = !!user.userProgramStates.find(ups =>
        getProgramConfigValue(ups.programType, 'supportsProgramApplications'),
    );
    if (!hasUpsThatSupportsApplications) return 'other';

    // FIXME: we'd like to put admins into the 'other' category, but it's kinda
    // hard to get access to that on BaseUser right now, because CurrentUserIguanaObject and User don't store roles in the same way.
    // Totally solvable, but I'm trying to get this out.

    const hasAcceptedAdmissionOffer = !!user.admissionOffers.find(
        ao => ao.status === AdmissionOfferStatus.AcceptedAdmissionOffer,
    );

    return hasAcceptedAdmissionOffer ? 'committed' : 'prospective';
}

export const hasExternalInstitution = (user: CurrentUser | null | undefined) =>
    !!user?.institutions.some(institution => institution.external);

export function getActivePlaylistLocalePackId(user: CurrentUser | null | undefined) {
    if (!user) return null;

    const prefUps = getUserProgramState(user);

    return prefUps?.activePlaylistLocalePackId || user.activePlaylistLocalePackId;
}
